import { useLocation, useSearchParams } from "react-router";
import { EmptyState, Field, Menu, Skeleton, ViewsNav, type MenuSection, type ViewGroup } from "@omnabase/ui";
import { RANGES, rangeFrom, type Range } from "@omnabase/shared";
import { Shell } from "../../shell/Shell";
import { useSession } from "../../shell/store";
import { useNav, useNavigate, useWs } from "../../lib/nav";
import { useScrollReset } from "../../lib/scrollReset";
import { ReportOverview } from "./ReportOverview";
import { ReportAi } from "./ReportAi";
import { ReportTeam } from "./ReportTeam";

/** Reports (M8 round two, task 1; ported from the prototype's ReportsScreen against the API): three pages in the views column, the period
 *  and the project scope in the URL and travelling between them, the page head's period menu, the empty state on a workspace's first day.
 *  Every number comes from the API, which applies the rules on the server; the screens format and draw. On a phone the views column is
 *  the area's first level (the shell's `?views=1`). */
const REPORTS = [
  { id: "overview", label: "Overview", icon: "chart", subtitle: "How much comes in, how fast it is answered, and what it is about." },
  { id: "ai", label: "AI performance", icon: "sparkles", subtitle: "What the AI did on its own, what people made of its drafts, and whether to trust it with more." },
  { id: "team", label: "Team", icon: "users", subtitle: "Who resolved what, and how long customers waited for a person." },
] as const;
type ReportId = (typeof REPORTS)[number]["id"];

/** what every page gets: the period, the scope, and an href that keeps the period and changes the rest */
export type ReportContext = { slug: string; range: Range; project: { id: string; name: string } | null; projects: { id: string; name: string }[]; multi: boolean; query: string; link: (path: string, projectId?: string | null) => string; canSeeSpend: boolean };

export function ReportsScreen() {
  const nav = useNav(); const navigate = useNavigate(); const { ws, slug } = useWs();
  const { pathname, search } = useLocation(); const [params] = useSearchParams();
  const { workspace } = useSession();
  const tail = pathname.slice(ws("/reports").length).replace(/^\//, "");
  const active = REPORTS.find((r) => r.id === tail) ?? REPORTS[0];
  const top = useScrollReset(active.id); // a report's page starts at the top (polish round one)
  const range = rangeFrom(params.get("range"), "30d");
  const live = workspace.projects.filter((p) => !p.archivedAt);
  const multi = live.length > 1;
  const project = multi ? live.find((p) => p.id === params.get("project")) ?? null : null;
  const path = (id: ReportId) => (id === "overview" ? "/reports" : `/reports/${id}`);
  // the period and the project travel from page to page: they live in the URL, the page is the path
  const to = (p: string, change: Record<string, string | null> = {}) => {
    const n = new URLSearchParams(search);
    for (const [k, v] of Object.entries(change)) { if (v) n.set(k, v); else n.delete(k); }
    const qs = n.toString();
    return ws(`${p}${qs ? `?${qs}` : ""}`);
  };
  const query = new URLSearchParams({ range: range.id, ...(project ? { project: project.id } : {}) }).toString();
  const ctx: ReportContext = { slug, range, project, projects: live, multi, query, link: (p, projectId) => to(p, projectId === undefined ? {} : { project: projectId }), canSeeSpend: false };
  const groups: ViewGroup[] = [{ phoneLabel: "Reports", items: REPORTS.map((r) => ({ id: r.id, label: r.label, icon: r.icon, href: to(path(r.id)), active: r.id === active.id })) }];
  const projectSection: MenuSection = { label: "Project", items: [
    { id: "all", label: "All projects", checked: !project, onSelect: () => navigate(to(path(active.id), { project: null })) },
    ...live.map((p) => ({ id: p.id, label: p.name, checked: project?.id === p.id, onSelect: () => navigate(to(path(active.id), { project: p.id })) })),
  ] };
  const scope = <Menu trigger={<Field label="Project" icon="package" value={project ? project.name : "All projects"} />} sections={[projectSection]} />;
  const period = (
    <Menu align="end" trigger={<Field label="Period" icon="calendar" value={range.label} />}
      sections={[{ label: "Period", items: RANGES.map((r) => ({ id: r.id, label: r.label, checked: r.id === range.id, onSelect: () => navigate(to(path(active.id), { range: r.id === "30d" ? null : r.id })) })) }]} />
  );
  const crumbs = [{ label: "Reports", href: ws("/reports") }, { label: active.label, views: true }]; // the page is the level the column selects: its way up is the first level on a phone, the sheet elsewhere
  return (
    <Shell views={<ViewsNav groups={groups} scope={multi ? scope : undefined} onNavigate={nav} hint="Numbers compare with the same number of days before the period." />} crumbs={crumbs} bodyClassName="ob-body--dashboard">
      <main className="ob-page ob-report" aria-label={active.label} ref={top}>
        <div className="ob-page__head">
          <div><h1 className="ob-page__title">{active.label}</h1><p className="ob-page__subtitle">{active.subtitle}</p></div>
          <div className="ob-page__filters">{period}</div>
        </div>
        {active.id === "ai" ? <ReportAi ctx={ctx} /> : active.id === "team" ? <ReportTeam ctx={ctx} /> : <ReportOverview ctx={ctx} />}
      </main>
    </Shell>
  );
}

/** "+8 % vs the 30 days before", or nothing when there is no earlier period */
export const versus = (delta: string | null, days: number) => (delta ? `${delta} vs the ${days} days before` : undefined);
/** the empty state every page shows on a workspace's first day */
export function NothingYet({ ctx }: { ctx: ReportContext }) {
  return <EmptyState icon="chart" title="Nothing to report yet" text={`${ctx.project ? ctx.project.name : "This workspace"} received no tickets in the ${ctx.range.label.toLowerCase()}. Numbers appear with the first ticket.`} />;
}

/** a report while its numbers load (polish round two): the tiles and a chart in grey, still blocks, so the page does not jump when they arrive */
export function ReportLoading() {
  return (
    <div className="ob-report__loading" aria-busy="true">
      <div className="ob-report__loading-tiles">{[0, 1, 2, 3].map((i) => <Skeleton key={i} shape="block" height={96} />)}</div>
      <Skeleton shape="card" height={260} />
    </div>
  );
}
